import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { HomePage } from './pages/HomePage';
import { ServicesPage } from './pages/ServicesPage';
import { ContactPage } from './pages/ContactPage';
import { ConsultationPage } from './pages/ConsultationPage';
import { AboutPage } from './pages/AboutPage';
import { ProjectPlannerPage } from './pages/ProjectPlannerPage';
import { BlogPage } from './pages/BlogPage';
import { BlogPostPage } from './pages/BlogPostPage';
import { BathroomRemodelingPage } from './pages/services/BathroomRemodelingPage';
import { KitchenRenovationPage } from './pages/services/KitchenRenovationPage';
import { HomeAdditionsPage } from './pages/services/HomeAdditionsPage';
import { WineCellarsPage } from './pages/services/WineCellarsPage';
import { PoolSpaPage } from './pages/services/PoolSpaPage';
import { HomeTheaterPage } from './pages/services/HomeTheaterPage';
import { InteriorRenovationsPage } from './pages/services/InteriorRenovationsPage';
import { ExteriorImprovementsPage } from './pages/services/ExteriorImprovementsPage';
import { BasementFinishingPage } from './pages/services/BasementFinishingPage';
import { CustomCarpentryPage } from './pages/services/CustomCarpentryPage';
import { DeckPatioPage } from './pages/services/DeckPatioPage';
import { GeneralRepairsPage } from './pages/services/GeneralRepairsPage';
import { NewConstructionPage } from './pages/services/NewConstructionPage';
import { CommercialConstructionPage } from './pages/services/CommercialConstructionPage';
import { ConstructionManagementPage } from './pages/services/ConstructionManagementPage';
import { RenovationsPage } from './pages/services/RenovationsPage';
import { DesignBuildPage } from './pages/services/DesignBuildPage';
import { AreaPage } from './pages/AreaPage';
import { Navigation } from './components/Navigation';
import { AIEstimateButton } from './components/AIEstimateButton';
import { trackEvent } from './lib/analytics';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AnalyticsPageView() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag !== 'function') return;

    const sendPageView = () => {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    };

    // Helmet updates the title after paint; wait one frame so GA gets the page title.
    const frame = window.requestAnimationFrame(sendPageView);
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.search]);

  return null;
}

function AnalyticsClicks() {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as Element | null)?.closest?.('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href') ?? '';
      const page_path = window.location.pathname;
      if (href.startsWith('tel:')) {
        trackEvent('phone_click', { page_path, link_url: href });
      } else if (href === '/consultation' || href.startsWith('/consultation?')) {
        trackEvent('consultation_click', { page_path });
      }
    };

    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  return null;
}

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      if (scrolled !== isScrolled) {
        setIsScrolled(scrolled);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <>
      <ScrollToTop />
      <AnalyticsPageView />
      <AnalyticsClicks />
      <Navigation isScrolled={isScrolled} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/consultation" element={<ConsultationPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/project-planner" element={<ProjectPlannerPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/areas/:slug" element={<AreaPage />} />
        
        {/* Core Construction Services */}
        <Route path="/services/new-construction" element={<NewConstructionPage />} />
        <Route path="/services/commercial-construction" element={<CommercialConstructionPage />} />
        <Route path="/services/construction-management" element={<ConstructionManagementPage />} />
        <Route path="/services/renovations" element={<RenovationsPage />} />
        <Route path="/services/design-build" element={<DesignBuildPage />} />
        
        {/* Specialized Services */}
        <Route path="/services/bathroom-remodeling-westchester" element={<BathroomRemodelingPage />} />
        <Route path="/services/kitchen-renovation-westchester" element={<KitchenRenovationPage />} />
        <Route path="/services/home-additions-westchester" element={<HomeAdditionsPage />} />
        <Route path="/services/wine-cellars-westchester" element={<WineCellarsPage />} />
        <Route path="/services/pool-spa-design-westchester" element={<PoolSpaPage />} />
        <Route path="/services/home-theater-westchester" element={<HomeTheaterPage />} />
        <Route path="/services/interior-renovations" element={<InteriorRenovationsPage />} />
        <Route path="/services/exterior-improvements" element={<ExteriorImprovementsPage />} />
        <Route path="/services/basement-finishing" element={<BasementFinishingPage />} />
        <Route path="/services/custom-carpentry" element={<CustomCarpentryPage />} />
        <Route path="/services/deck-patio-construction" element={<DeckPatioPage />} />
        <Route path="/services/general-repairs" element={<GeneralRepairsPage />} />
      </Routes>
      <AIEstimateButton />
    </>
  );
}

export default App;